import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import type { LWApiPublicSupportRequestDetailsResultRawI } from '@liquidweb-dev/club-sauce/public/support/request/index.raw';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce/types/boolean';
import Chip from '@liquidweb-dev/undercurrent/Chip';
import { pseudoClasses } from '@liquidweb-dev/undercurrent/classes/pseudo';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import EyeIcon from '@liquidweb-dev/undercurrent/icons/lucide/Eye';
import MessageSquareIcon from '@liquidweb-dev/undercurrent/icons/lucide/MessageSquare';
import ListItemRaw, {
	ListItemProps,
} from '@liquidweb-dev/undercurrent/ListItem';
import ListItemButton from '@liquidweb-dev/undercurrent/ListItemButton';
import ListItemText from '@liquidweb-dev/undercurrent/ListItemText';
import ListSubheader from '@liquidweb-dev/undercurrent/ListSubheader';
import Stack, { StackProps } from '@liquidweb-dev/undercurrent/Stack';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import TypographyOverflow from '@liquidweb-dev/undercurrent/TypographyOverflow';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import StatusDot from 'components/icons/StatusDot';
import DrawerCasePreview from 'components/standard/DrawerCasePreview';

const ListItem = ({
	sx,
	children,
	to,
	...rest
}: ListItemProps & { to?: string }) => {
	const history = useHistory();
	return (
		<ListItemRaw
			sx={{
				[`:hover, &:has(*:focus-visible)`]: {
					// Show preview button on hover and any focusable element
					'.cases-list-item-preview-button': {
						opacity: 1,
					},
				},
				...sx,
			}}
			{...rest}
		>
			<ListItemButton
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: 100,
				}}
				onClick={() => {
					if (to) {
						history.push(to);
					}
				}}
			>
				{children}
			</ListItemButton>
		</ListItemRaw>
	);
};

interface LastReplyChipProps {
	lastReply: string;
}

export const LastReplyChip = ({ lastReply }: LastReplyChipProps) => {
	return (
		<Tooltip title={`Last reply ${dayjs(lastReply).format('l LT')}`}>
			<Chip icon={<MessageSquareIcon />} label={dayjs(lastReply).fromNow()} />
		</Tooltip>
	);
};

interface CreatedDateProps {
	createdDate: string;
}

export const CreatedDate = ({ createdDate }: CreatedDateProps) => {
	return (
		<Tooltip title={`Created ${dayjs(createdDate).format('l LT')}`}>
			<Typography
				variant="label-md"
				sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
			>
				{dayjs(createdDate).format('l')}
			</Typography>
		</Tooltip>
	);
};

interface DomainChipProps {
	domain: string;
}

const DomainChip = ({ domain }: DomainChipProps) => {
	return (
		<Tooltip title={domain}>
			<Chip label={domain} sx={combineSx(() => ({ maxWidth: '150px' }))} />
		</Tooltip>
	);
};

interface PreviewButtonProps {
	onClick: React.MouseEventHandler<HTMLButtonElement>;
}

const PreviewButton = ({ onClick }: PreviewButtonProps) => (
	<Tooltip title="Preview">
		<IconButton
			variant="barebones"
			size="large"
			className="cases-list-item-preview-button"
			sx={{
				opacity: 0,
				transition: 'opacity 0.1s ease-in-out',
				padding: 0,
				':focus-visible': {
					opacity: 1,
				},
				display: { xs: 'none', md: 'inline-flex' },
			}}
			onClick={onClick}
		>
			<EyeIcon
				sx={{
					color: ({ vars }) => vars.palette.uc.icon.main,
					[pseudoClasses.important]: {
						height: ({ size }) => size.sm,
						width: ({ size }) => size.sm,
					},
				}}
			/>
		</IconButton>
	</Tooltip>
);

const AttributesWrapper = ({ sx, ...rest }: StackProps) => {
	return (
		<Stack
			direction="row"
			gap={100}
			alignItems="center"
			sx={combineSx({ display: { xs: 'none', md: 'flex' } }, sx)}
			{...rest}
		/>
	);
};

const TitleWrapper = ({ ...rest }: StackProps) => (
	<Stack direction="row" gap={200} alignItems="center" minWidth={0} {...rest} />
);

const TitleSecondaryWrapper = ({ ...rest }: StackProps) => (
	<Stack direction="row" gap={100} alignItems="center" minWidth={0} {...rest} />
);

interface CaseProps {
	title: string;
	items: LWApiPublicSupportRequestDetailsResultRawI[];
	path: string;
}

const Cases = ({ title, items, path }: CaseProps) => {
	const [caseToPreview, setCaseToPreview] = useState<string>('');
	const caseToPreviewDetails = useMemo(() => {
		if (!caseToPreview) return undefined;
		return items.find(
			({ ticket_number: ticketNumber }) =>
				ticketNumber?.toString() === caseToPreview,
		);
	}, [items, caseToPreview]);

	const getVariant = (status: string | undefined) => {
		switch (status) {
			case 'Closed':
				return 'neutral';
			case 'Waiting on Customer':
				return 'warning';
			default:
				return 'success';
		}
	};

	return (
		<>
			<Stack>
				<ListSubheader>
					<Typography variant="body-sm">
						{title}{' '}
						<Typography
							variant="body-sm"
							component="span"
							sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
						>
							{items.length}
						</Typography>
					</Typography>
				</ListSubheader>
				{items.map(
					({
						id,
						status,
						subject,
						ticket_number: ticketNumber,
						last_outbound_email: lastReply,
						created_date: createdDate,
						subaccntHashRef,
					}) => (
						<ListItem
							key={id}
							// @ts-ignore
							to={
								path === 'migration-center'
									? `/support/mycases/${ticketNumber}?fromMigrationList=true`
									: `/support/mycases/${ticketNumber}`
							}
						>
							<TitleWrapper>
								<Tooltip title={status}>
									<IconButton variant="barebones" size="large">
										<StatusDot variant={getVariant(status)} />
									</IconButton>
								</Tooltip>

								<TitleSecondaryWrapper>
									<ListItemText
										primary={<TypographyOverflow>{subject}</TypographyOverflow>}
										secondary={ticketNumber}
										primaryTypographyProps={{ minWidth: 0 }}
									/>
									<PreviewButton
										onClick={(event) => {
											event.stopPropagation();
											setCaseToPreview(String(ticketNumber));
										}}
									/>
								</TitleSecondaryWrapper>
							</TitleWrapper>

							<AttributesWrapper>
								{subaccntHashRef?.domain && (
									<DomainChip domain={subaccntHashRef.domain} />
								)}
								{lastReply && <LastReplyChip lastReply={lastReply} />}
								{createdDate && <CreatedDate createdDate={createdDate} />}
							</AttributesWrapper>
						</ListItem>
					),
				)}
			</Stack>
			<DrawerCasePreview
				onClose={() => setCaseToPreview('')}
				open={Boolean(caseToPreview)}
				ticketNumber={caseToPreview}
				status={caseToPreviewDetails?.status || ''}
				subject={caseToPreviewDetails?.subject || ''}
				createdDate={caseToPreviewDetails?.created_date || ''}
				caseId={caseToPreviewDetails?.id || ''}
				description={caseToPreviewDetails?.description || ''}
				respondable={fromApiBoolean(caseToPreviewDetails?.respondable)}
				subaccntHashRef={caseToPreviewDetails?.subaccntHashRef}
				desiredLwTeam={caseToPreviewDetails?.desired_lw_team}
				subType={caseToPreviewDetails?.sub_type}
				ccList={caseToPreviewDetails?.cc_list}
				path={path}
			/>
		</>
	);
};

export default Cases;
